import React, { useState, useEffect, useContext } from 'react';
import AuthContext from '../context/AuthContext';
import axios from 'axios';
import EventCard from './EventCard';
import ModernCarousel from './ModernCarousel';
import { EventStack, EventStackModal } from './EventStack';
import { Box, Container, Typography, CircularProgress, Alert } from '@mui/material';
import { ArrowRightIcon, ChevronRightIcon } from './Icons';

const UpcomingEvents = ({ header }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedStack, setSelectedStack] = useState(null);
  const { fetchPublicData, PUBLIC_API_URL } = useContext(AuthContext);

  const title = header?.title || 'Kommende Aktionen';
  const subtitle = header?.subtitle || 'Sei dabei! Hier findest du alle Termine für unsere nächsten Gruppenstunden, Lager und Ausflüge.';

  // Expansion and Pagination State
  const [isExpanded, setIsExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(9); // Default 3 rows of 3 on desktop

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const data = await fetchPublicData('/events?upcoming=true');
        if (Array.isArray(data)) {
          setEvents(data);
        } else {
          setEvents([]);
        }
      } catch (err) {
        console.error("Error fetching events:", err);
        setError("Events konnten nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [fetchPublicData]);

  // Group events by date (same calendar day)
  const groupEventsByDate = (events) => {
    const groups = {};

    events.forEach(event => {
      const date = new Date(event.date).toISOString().split('T')[0];
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(event);
    });

    // Sort events inside each group by start time
    Object.keys(groups).forEach(date => {
      groups[date].sort((a, b) => {
        const timeA = a.startTime || '00:00';
        const timeB = b.startTime || '00:00';
        return timeA.localeCompare(timeB);
      });
    });

    // Sort groups chronologically
    return Object.keys(groups)
      .sort((a, b) => a.localeCompare(b))
      .map(date => groups[date]);
  };

  const eventGroups = groupEventsByDate(events);

  const handleLoadMore = () => {
    if (!isExpanded) {
      setIsExpanded(true);
    } else {
      setVisibleCount(prev => prev + 9);
    }
  };

  const hasMore = isExpanded ? eventGroups.length > visibleCount : eventGroups.length > 3;

  return (
    <Box component="section" id="events-section" sx={{ py: 12, position: 'relative', overflow: 'hidden', bgcolor: 'rgba(var(--color-surface-container-low), 1)' }}>
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ mb: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="h3" component="h2" fontWeight="bold" sx={{ color: 'text.primary' }}>
              {title}
            </Typography>
             <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: '42rem' }}>
              {subtitle}
            </Typography>

          </Box>

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="md3-btn-icon-unfilled w-12 h-12 flex items-center justify-center rounded-full bg-surface-containerHigh text-surface-on hover:bg-primary/10 hover:text-primary transition-all duration-300 shadow-elevation-1"
            aria-label={isExpanded ? "Sektion einklappen" : "Sektion ausklappen"}
            style={{ border: 'none', cursor: 'pointer' }}
          >
            <ChevronRightIcon
              className={`transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`}
              sx={{ fontSize: 32 }}
            />
          </button>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Container maxWidth="sm" sx={{ my: 4 }}>
            <Alert severity="error">{error}</Alert>
          </Container>
        )}

        {!loading && !error && events.length === 0 && (
          <Typography align="center" color="text.secondary" fontStyle="italic">
            Aktuell sind keine öffentlichen Termine geplant.
          </Typography>
        )}

        {!loading && !error && events.length > 0 && (
          <>
            <div className="transition-all duration-500 ease-in-out">
              {!isExpanded ? (
                <ModernCarousel>
                  {eventGroups.map((group, index) => (
                    <React.Fragment key={group.length > 1 ? `stack-${index}` : group[0]._id}>
                      {group.length > 1 ? (
                        <EventStack
                          events={group}
                          onClick={() => setSelectedStack(group)}
                        />
                      ) : (
                        <EventCard event={group[0]} />
                      )}
                    </React.Fragment>
                  ))}
                </ModernCarousel>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 animate-fade-in">
                  {eventGroups.slice(0, visibleCount).map((group, index) => (
                    <div key={group.length > 1 ? `stack-grid-${index}` : group[0]._id} className="h-full">
                      {group.length > 1 ? (
                        <EventStack
                          events={group}
                          onClick={() => setSelectedStack(group)}
                        />
                      ) : (
                        <EventCard event={group[0]} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {hasMore && (
              <div className="mt-16 text-center">
                <button
                  onClick={handleLoadMore}
                  className="md3-btn-filled rounded-full h-14 px-12 gap-3 shadow-elevation-2 hover:shadow-elevation-4 hover:scale-105 transition-all group"
                >
                  {isExpanded ? 'Noch mehr laden' : 'Alle Aktionen anzeigen'}
                  <ArrowRightIcon className={`transition-transform duration-300 ${isExpanded ? 'rotate-90' : 'group-hover:translate-x-1'}`} style={{ fontSize: 20 }} />
                </button>
              </div>
            )}

            {isExpanded && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => setIsExpanded(false)}
                  className="md3-btn-text text-surface-onVariant hover:text-primary transition-colors font-medium"
                >
                  Weniger anzeigen
                </button>
              </div>
            )}
          </>
        )}
      </Container>

      {selectedStack && (
        <EventStackModal
          events={selectedStack}
          onClose={() => setSelectedStack(null)}
        />
      )}
    </Box>
  );
};

export default UpcomingEvents;
